// Public marketing landing page for the YouTube SMM reselling feature.
//
// Matches the design language of MetadeckLanding / OptimizedLandingPage (hero,
// trust/benefit sections, pricing, FAQ, CTAs; brand orange). The route for this
// page is exposed at the canonical public /youtube route.
//
// Pricing shown here is DISPLAY data from the shared constant
// `frontend/constants/youtubeSmmPricing.ts` (the single editable place), including
// each product's availability state.

import React, { useEffect, useState } from 'react';
import {
  ArrowRight, Check, Plus, Minus, Star, Zap, Shield, RefreshCw, Headphones, Play,
} from 'lucide-react';
import { brandConfig } from '../../config/brand';
import SEO from '../SEO';
import {
  YOUTUBE_SMM_PRODUCTS,
  formatQty,
  formatUsd,
  pricePer1k,
  type YtProduct,
} from '../../constants/youtubeSmmPricing';

interface Props {
  onLogin: () => void;
  onNavigate: (path: string) => void;
}

const C = {
  orange: brandConfig.primaryColor,
  orangeHover: brandConfig.primaryHover,
  orangeLight: '#FFF3EE',
};

const BENEFITS = [
  { icon: <Zap size={20} />, title: 'Fast delivery', desc: 'Orders start within minutes of checkout — no waiting around for your views to begin.' },
  { icon: <Shield size={20} />, title: 'Safe & private', desc: 'We never ask for your password. Just your public video link is all we need.' },
  { icon: <RefreshCw size={20} />, title: 'Refill guarantee', desc: 'Eligible packages include a refill window so your numbers stay where they should.' },
  { icon: <Headphones size={20} />, title: '24/7 support', desc: 'Real humans on chat and email, any time you need a hand with an order.' },
];

const STEPS = [
  { step: 'Step 1', title: 'Pick a package', desc: 'Choose a views package, or set a custom quantity that fits your goal.' },
  { step: 'Step 2', title: 'Paste your link', desc: 'Drop in your public YouTube video URL — no login or password required.' },
  { step: 'Step 3', title: 'Watch it grow', desc: 'Delivery starts within minutes. Track progress right from your dashboard.' },
];

const FAQS = [
  { q: 'Are these real views?', a: 'Yes. We deliver high-retention views from real traffic sources, designed to look natural and support your video ranking.' },
  { q: 'Do you need my password?', a: 'Never. We only need your public YouTube video URL. We will never ask for your account password or any login details.' },
  { q: 'How fast does delivery start?', a: 'Most orders begin within minutes of checkout and complete at a natural pace to keep things safe.' },
  { q: 'Is there a refill guarantee?', a: 'Eligible packages come with a refill window — if numbers drop within that window we top them back up at no extra cost.' },
  { q: 'When will subscribers, likes and watch hours launch?', a: 'Views are available today. Subscribers, likes, and watch-hour packages are coming soon — create an account to be notified first.' },
];

const TESTIMONIALS = [
  { name: 'Marcus T.', role: 'Music creator', body: 'Ordered 10K views before a release and they showed up fast. Clean delivery, no weird drops.' },
  { name: 'Lena K.', role: 'Gaming channel', body: 'The custom quantity option is perfect for topping up specific videos. Dashboard makes it easy to track.' },
  { name: 'Ravi S.', role: 'Tutorial channel', body: 'Used the 25K pack for a launch. Support answered within minutes when I had a question.' },
];

export const YOUTUBE_PAGE_TITLE = 'Buy YouTube Views with Fast Delivery | Traffic Creator';
export const YOUTUBE_PAGE_DESCRIPTION = 'Buy YouTube views with fast delivery, no password required, dashboard order tracking, and eligible refill coverage. Compare transparent USD package pricing.';
export const YOUTUBE_PAGE_URL = 'https://traffic-creator.com/youtube';
export const YOUTUBE_PAGE_IMAGE = 'https://traffic-creator.com/img/blog/introducing-youtube-views-service-new.png';

const youtubeOffers = YOUTUBE_SMM_PRODUCTS
  .filter((product) => product.available)
  .flatMap((product) => product.tiers.map((tier) => ({
    '@type': 'Offer',
    name: `${formatQty(tier.quantity)} ${product.unit}`,
    url: `${YOUTUBE_PAGE_URL}#pricing`,
    price: tier.price.toFixed(2),
    priceCurrency: 'USD',
    availability: 'https://schema.org/InStock',
    itemOffered: {
      '@type': 'Service',
      name: product.name,
      description: product.tagline,
    },
  })));

export const youtubeLandingSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': 'https://traffic-creator.com/#organization',
      name: 'Traffic Creator',
      url: 'https://traffic-creator.com/',
      email: brandConfig.supportEmail,
    },
    {
      '@type': 'WebSite',
      '@id': 'https://traffic-creator.com/#website',
      url: 'https://traffic-creator.com/',
      name: 'Traffic Creator',
      publisher: { '@id': 'https://traffic-creator.com/#organization' },
    },
    {
      '@type': 'WebPage',
      '@id': `${YOUTUBE_PAGE_URL}#webpage`,
      url: YOUTUBE_PAGE_URL,
      name: YOUTUBE_PAGE_TITLE,
      description: YOUTUBE_PAGE_DESCRIPTION,
      isPartOf: { '@id': 'https://traffic-creator.com/#website' },
      breadcrumb: { '@id': `${YOUTUBE_PAGE_URL}#breadcrumb` },
      mainEntity: { '@id': `${YOUTUBE_PAGE_URL}#service` },
      primaryImageOfPage: { '@id': `${YOUTUBE_PAGE_URL}#primaryimage` },
    },
    {
      '@type': 'ImageObject',
      '@id': `${YOUTUBE_PAGE_URL}#primaryimage`,
      url: YOUTUBE_PAGE_IMAGE,
      contentUrl: YOUTUBE_PAGE_IMAGE,
      caption: 'Traffic Creator YouTube view packages',
    },
    {
      '@type': 'Service',
      '@id': `${YOUTUBE_PAGE_URL}#service`,
      name: 'Traffic Creator YouTube engagement packages',
      serviceType: 'YouTube channel promotion services',
      description: YOUTUBE_PAGE_DESCRIPTION,
      url: YOUTUBE_PAGE_URL,
      provider: { '@id': 'https://traffic-creator.com/#organization' },
      areaServed: 'Worldwide',
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'YouTube engagement packages',
        itemListElement: youtubeOffers,
      },
    },
    {
      '@type': 'FAQPage',
      '@id': `${YOUTUBE_PAGE_URL}#faq`,
      mainEntity: FAQS.map((faq) => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.a,
        },
      })),
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${YOUTUBE_PAGE_URL}#breadcrumb`,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://traffic-creator.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'YouTube services',
          item: YOUTUBE_PAGE_URL,
        },
      ],
    },
  ],
};

const PricingCard: React.FC<{ product: YtProduct }> = ({ product }) => {
  const cheapestPer1k = Math.min(...product.tiers.map(pricePer1k));
  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-[0_1px_6px_rgba(0,0,0,0.04)] overflow-hidden flex flex-col">
      <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-extrabold text-gray-900">{product.name}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{product.tagline}</p>
        </div>
        {product.available ? (
          <span className="text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full whitespace-nowrap"
            style={{ background: C.orangeLight, color: C.orange }}>
            Available
          </span>
        ) : (
          <span className="text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-gray-100 text-gray-400 whitespace-nowrap">
            Coming soon
          </span>
        )}
      </div>
      <div className="p-4 flex-1">
        <ul className="space-y-2">
          {product.tiers.map((t) => {
            const isBest = pricePer1k(t) === cheapestPer1k;
            return (
              <li
                key={t.quantity}
                className="flex items-center justify-between rounded-lg border px-4 py-3"
                style={{
                  borderColor: isBest && product.available ? C.orange : '#f1f3f5',
                  background: isBest && product.available ? C.orangeLight : '#fff',
                }}
              >
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-900">{formatQty(t.quantity)}</span>
                  <span className="text-xs text-gray-400">{product.unit}</span>
                  {t.badge && (
                    <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded"
                      style={{ background: C.orange, color: '#fff' }}>
                      {t.badge}
                    </span>
                  )}
                </div>
                <span className="font-extrabold" style={{ color: product.available ? C.orange : '#9ca3af' }}>
                  {formatUsd(t.price)}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

const YouTubeLanding: React.FC<Props> = ({ onNavigate }) => {
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  useEffect(() => {
    const handle = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', handle);
    return () => window.removeEventListener('scroll', handle);
  }, []);

  const goBuy = () => onNavigate('dashboard/youtube-smm');
  const goSignup = () => onNavigate('signup');

  return (
    <>
      <SEO
        title={YOUTUBE_PAGE_TITLE}
        description={YOUTUBE_PAGE_DESCRIPTION}
        keywords="buy YouTube views, YouTube view packages, YouTube engagement, YouTube promotion, Traffic Creator YouTube"
        image={YOUTUBE_PAGE_IMAGE}
        schema={youtubeLandingSchema}
      />
      <div className="min-h-screen bg-white text-gray-900 antialiased"
        style={{ fontFamily: "'Inter','DM Sans',-apple-system,BlinkMacSystemFont,sans-serif" }}>

      {/* NAV */}
      <header className={`sticky top-0 z-40 bg-white transition-all duration-200 ${scrolled ? 'shadow-[0_1px_12px_rgba(0,0,0,0.07)]' : ''} border-b border-gray-100`}>
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => onNavigate('youtube')} className="flex items-center gap-2 focus:outline-none">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: C.orange }}>
              <Play size={16} className="text-white" fill="#fff" />
            </div>
            <span className="font-extrabold text-xl tracking-tight text-gray-900">
              {brandConfig.shortName} <span style={{ color: C.orange }}>YouTube</span>
            </span>
          </button>
          <div className="flex items-center gap-3">
            <button onClick={() => onNavigate('login')} className="hidden sm:flex text-sm font-semibold text-gray-600 hover:text-gray-900 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors">
              Log In
            </button>
            <button onClick={goSignup} className="text-sm font-bold text-white px-4 py-2 rounded-lg shadow-sm hover:opacity-90 transition-opacity" style={{ background: C.orange }}>
              Get Started
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-12 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6 text-xs font-semibold border border-orange-200"
          style={{ background: C.orangeLight, color: C.orange }}>
          <Star size={12} fill={C.orange} color={C.orange} /> Trusted by creators worldwide
        </div>
        <h1 className="text-4xl md:text-6xl font-extrabold leading-[1.05] tracking-tight text-gray-900 mb-5">
          Grow your YouTube channel<br />
          with <span style={{ color: C.orange }}>real engagement</span>
        </h1>
        <p className="text-lg text-gray-500 mb-8 max-w-2xl mx-auto leading-relaxed">
          Buy YouTube views, subscribers, likes and watch hours — fast delivery, safe by design,
          and a refill guarantee. No password, ever. Just your video link.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
          <button onClick={goBuy}
            className="px-8 py-3.5 rounded-xl font-bold text-white text-base shadow-lg hover:-translate-y-0.5 transition-all inline-flex items-center justify-center gap-2"
            style={{ background: C.orange }}
            onMouseOver={(e) => (e.currentTarget.style.background = C.orangeHover)}
            onMouseOut={(e) => (e.currentTarget.style.background = C.orange)}>
            Buy YouTube Views <ArrowRight size={18} />
          </button>
          <button onClick={() => {
            document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
          }}
            className="px-8 py-3.5 rounded-xl font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 text-base transition-colors">
            See pricing
          </button>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-5 text-sm text-gray-500">
          {['Fast delivery', 'No password needed', 'Refill guarantee', '24/7 support'].map((t) => (
            <span key={t} className="inline-flex items-center gap-1.5">
              <Check size={14} style={{ color: C.orange }} /> {t}
            </span>
          ))}
        </div>
      </section>

      {/* BENEFITS */}
      <section className="bg-gray-50 border-y border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {BENEFITS.map((b) => (
              <div key={b.title} className="bg-white rounded-xl border border-gray-200 p-6 shadow-[0_1px_4px_rgba(0,0,0,0.03)]">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 text-white" style={{ background: C.orange }}>
                  {b.icon}
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-1.5">{b.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-2">How it works</h2>
          <p className="text-gray-500">From checkout to growing numbers in under 5 minutes.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {STEPS.map((s, i) => (
            <div key={i} className="relative bg-white rounded-2xl border border-gray-200 p-7 shadow-[0_1px_6px_rgba(0,0,0,0.04)]">
              {i < STEPS.length - 1 && (
                <div className="hidden md:block absolute top-10 -right-3 w-6 h-px bg-gray-300 z-10" />
              )}
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 text-white font-black" style={{ background: C.orange }}>
                {i + 1}
              </div>
              <div className="text-xs font-black uppercase tracking-widest mb-2" style={{ color: C.orange }}>{s.step}</div>
              <h3 className="text-base font-bold text-gray-900 mb-2">{s.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="bg-gray-50 border-y border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-2">Simple, transparent pricing</h2>
            <p className="text-gray-500">
              Package tiers with volume discounts. Views available now — more products launching soon.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {YOUTUBE_SMM_PRODUCTS.map((p) => (
              <PricingCard key={p.id} product={p} />
            ))}
          </div>
          <div className="text-center mt-10">
            <button onClick={goBuy}
              className="px-8 py-3.5 rounded-xl font-bold text-white text-base shadow-lg hover:-translate-y-0.5 transition-all inline-flex items-center gap-2"
              style={{ background: C.orange }}>
              Buy YouTube Views now <ArrowRight size={18} />
            </button>
            <p className="text-xs text-gray-400 mt-3">Need a custom amount? Set any quantity at checkout.</p>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-2">Loved by creators</h2>
          <div className="flex items-center justify-center gap-1">
            {[...Array(5)].map((_, j) => <Star key={j} size={16} fill={C.orange} color={C.orange} />)}
            <span className="text-sm text-gray-500 font-semibold ml-2">4.8 / 5 average rating</span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 shadow-[0_1px_6px_rgba(0,0,0,0.04)]">
              <div className="flex gap-0.5 mb-3">
                {[...Array(5)].map((_, j) => <Star key={j} size={12} fill={C.orange} color={C.orange} />)}
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">"{t.body}"</p>
              <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ background: C.orange }}>
                  {t.name[0]}
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-900">{t.name}</div>
                  <div className="text-xs text-gray-400">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-gray-50 border-y border-gray-200">
        <div className="max-w-3xl mx-auto px-6 py-16">
          <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-8 text-center">Frequently asked questions</h2>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-[0_1px_4px_rgba(0,0,0,0.03)]">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors group"
                >
                  <span className="text-sm font-semibold text-gray-900 pr-4">{faq.q}</span>
                  <div className="w-6 h-6 rounded-full border border-gray-200 flex items-center justify-center shrink-0"
                    style={openFaq === i ? { background: C.orange, borderColor: C.orange } : {}}>
                    {openFaq === i ? <Minus size={12} className="text-white" /> : <Plus size={12} className="text-gray-500" />}
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 border-t border-gray-100">
                    <p className="text-sm text-gray-500 leading-relaxed pt-4">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="rounded-2xl p-10 md:p-14 text-center" style={{ background: `linear-gradient(135deg, ${C.orange} 0%, #FF8C42 100%)` }}>
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-3 leading-tight">Ready to grow your channel?</h2>
          <p className="text-orange-100 mb-8 text-base">Pick a package and start growing in minutes — no password required.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={goBuy}
              className="bg-white font-bold text-sm px-8 py-3.5 rounded-xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 inline-flex items-center justify-center gap-2"
              style={{ color: C.orange }}>
              Buy YouTube Views <ArrowRight size={16} />
            </button>
            <button onClick={goSignup}
              className="font-bold text-sm px-8 py-3.5 rounded-xl border border-white/60 text-white hover:bg-white/10 transition-colors">
              Create free account
            </button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-gray-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: C.orange }}>
              <Play size={13} className="text-white" fill="#fff" />
            </div>
            <span className="font-bold text-gray-900">{brandConfig.name}</span>
          </div>
          <span className="text-xs text-gray-400">© 2026 {brandConfig.name}. {brandConfig.supportEmail}</span>
        </div>
      </footer>
      </div>
    </>
  );
};

export default YouTubeLanding;
